import type { ReactNode } from 'react';

/**
 * Các mảnh giao diện dùng chung.
 *
 * Lớp Tailwind lấy nguyên văn từ DOM của bản đang chạy (đọc qua CDP
 * ngày 14/09/2026), không viết lại theo cảm nhận. Gom vào đây để tám màn
 * dùng chung một kiểu, thay vì mỗi màn tự chế một dáng thẻ khác nhau.
 */

const VIEN_THE =
  'rounded-xl border border-slate-200/80 bg-white shadow-card dark:border-slate-700/60 dark:bg-slate-800';

/** Khung thẻ chuẩn. Mọi khối nội dung trong dashboard đều nằm trong cái này. */
export function The({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={VIEN_THE + ' ' + className}>{children}</div>;
}

/** Đầu thẻ: biểu tượng + tiêu đề + số đếm + chỗ để nút bên phải. */
export function DauThe({
  icon, tieuDe, dem, phai,
}: { icon: ReactNode; tieuDe: string; dem?: number | string; phai?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5 dark:border-slate-700/60">
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-slate-400">{icon}</span>
        <h3 className="truncate text-sm font-bold text-slate-900 dark:text-white">{tieuDe}</h3>
        {dem !== undefined && (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {dem}
          </span>
        )}
      </div>
      {phai && <div className="shrink-0">{phai}</div>}
    </div>
  );
}

/**
 * Thẻ số liệu: biểu tượng, con số lớn, nhãn nhỏ bên dưới.
 *
 * ===== VÌ SAO CÓ PROP `dangTai` =====
 *
 * Con số ở đây luôn được suy ra từ một mảng trong state, mà mảng đó khởi tạo
 * rỗng. Nên trong khoảng thời gian API chưa trả về, thẻ hiện `0` — một con số
 * trông y hệt câu trả lời thật.
 *
 * Đo trên máy thật ngày 21/09/2026: `api.nhom()` và `api.chienDich()` khai hạn
 * giờ 20 giây (api.ts:112, api.ts:138). Người dùng có nhóm đã đồng bộ vẫn nhìn
 * thấy "0 Nhóm đã đồng bộ" suốt quãng chờ đó, rồi con số mới nhảy lên.
 *
 * Hậu quả không chỉ là khó chịu: `0` ở đây là lời khẳng định SAI. Người dùng
 * kết luận mình mất dữ liệu và đi đồng bộ lại — thao tác nặng, hoàn toàn thừa.
 *
 * `—` cũng không dùng được: nó là giá trị thật cho ô "chưa rõ" (TongQuan.tsx:188
 * dùng đúng nghĩa đó). Khung xương nhấp nháy thì không thể nhầm với dữ liệu.
 *
 * Quy tắc: thẻ nào lấy số từ state mảng thì BẮT BUỘC truyền `dangTai`.
 * Thẻ mang giá trị đã biết chắc ngay từ đầu thì không cần.
 */
export function TheSoLieu({
  icon, so, nhan, dangTai,
}: { icon: ReactNode; so: ReactNode; nhan: string; dangTai?: boolean }) {
  return (
    <div className={VIEN_THE + ' p-4'}>
      <span className="text-slate-400">{icon}</span>
      {dangTai ? (
        // Giữ đúng chiều cao dòng số (text-2xl) để trang không nhảy khi số về.
        <div className="mt-3 flex h-8 items-center" aria-busy="true">
          <span className="sr-only">Đang tải {nhan}</span>
          <span
            aria-hidden="true"
            className="h-6 w-16 animate-pulse rounded bg-slate-200 dark:bg-slate-700"
          />
        </div>
      ) : (
        <p className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">{so}</p>
      )}
      <p className="text-xs text-slate-500 dark:text-slate-400">{nhan}</p>
    </div>
  );
}

/**
 * Vòng xoay nhỏ báo "đang chạy". Dùng chung cho mọi nút.
 *
 * aria-hidden vì nghĩa của nó đã nằm ở `aria-busy` trên chính nút; đọc màn hình
 * không cần đọc thêm một hình trang trí.
 */
function VongXoay() {
  return (
    <span
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"
    />
  );
}

/**
 * ===== VÌ SAO NÚT PHẢI TỰ KHOÁ KHI ĐANG CHẠY =====
 *
 * Trước 21/09/2026, hai nút dùng chung chỉ nhận `disabled`. Mỗi màn phải tự nhớ
 * tạo state "đang gửi", tự đổi nhãn, tự khoá. Rà soát ngày 21/09/2026 đếm được
 * 7 nút GHI DỮ LIỆU quên làm việc đó — trong đó có nút "Đồng bộ lại" gọi API có
 * hạn giờ tới 90 giây. Bấm hai lần là hai yêu cầu đồng bộ nặng cùng chạy.
 *
 * Nguy hiểm hơn: "Tạo bộ sưu tập" và "Gán nhóm" là thao tác GHI. Bấm nhanh hai
 * lần tạo ra bản ghi trùng mà người dùng không hề biết.
 *
 * Nay `dangChay` làm ba việc một lúc: khoá nút, hiện vòng xoay, và đặt
 * `aria-busy` cho trình đọc màn hình. Màn gọi chỉ cần truyền một cờ.
 *
 * ⛔ KHOÁ Ở ĐÂY LÀ LỚP THỨ HAI, KHÔNG PHẢI LỚP DUY NHẤT.
 *
 * React đặt lại state theo lô và chỉ khoá sau khi render xong. Hai cú nhấp cách
 * nhau vài mili-giây có thể lọt cả hai trước khi nút kịp mờ đi. Lớp thứ nhất
 * phải là khoá đồng bộ NGAY trong handler — xem `useKhoaGui` bên dưới.
 * Đây là yêu cầu của UI-01 trong PROJECT_CONTEXT.md.
 */
export function NutPhu({
  children, onClick, disabled, dangChay,
}: { children: ReactNode; onClick?: () => void; disabled?: boolean; dangChay?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || dangChay}
      aria-busy={dangChay || undefined}
      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/50"
    >
      {dangChay && <VongXoay />}
      {children}
    </button>
  );
}

export function NutChinh({
  children, onClick, disabled, dangChay,
}: { children: ReactNode; onClick?: () => void; disabled?: boolean; dangChay?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || dangChay}
      aria-busy={dangChay || undefined}
      className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {dangChay && <VongXoay />}
      {children}
    </button>
  );
}

/** Nhãn trạng thái tròn, có chấm màu bên trái. */
export function NhanTrangThai({
  chu, mau,
}: { chu: string; mau: 'xanh' | 'xam' | 'vang' | 'do' }) {
  const nen = {
    xanh: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
    xam: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
    vang: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
    do: 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400',
  }[mau];
  const cham = { xanh: 'bg-emerald-500', xam: 'bg-slate-400', vang: 'bg-amber-500', do: 'bg-rose-500' }[mau];
  return (
    <span className={'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ' + nen}>
      <span className={'h-1.5 w-1.5 rounded-full ' + cham} />
      {chu}
    </span>
  );
}

/**
 * Ảnh đại diện có đường lui.
 *
 * Vì sao cần: ảnh Facebook có hạn dùng (tham số oe= trong URL) và sẽ hỏng sau
 * vài ngày. Bản cũ để thẻ <img> gãy, hiện ô trống méo mó. Ở đây hỏng thì hiện
 * chữ cái đầu của tên — vẫn nhận ra được là tài khoản nào.
 */
export function AnhDaiDien({
  src, ten, kichThuoc = 'h-9 w-9',
}: { src?: string | null; ten: string; kichThuoc?: string }) {
  const chu = (ten || '?').trim().slice(0, 1).toUpperCase();
  return (
    <div className={'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700 ' + kichThuoc}>
      {src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <span className="text-xs font-bold text-slate-500 dark:text-slate-300">{chu}</span>
      )}
    </div>
  );
}

/** Khối thông báo khi chưa có dữ liệu. Nói rõ phải làm gì, không chỉ báo trống. */
export function ChuaCoDuLieu({ chu, goiY }: { chu: string; goiY?: string }) {
  return (
    <div className="px-5 py-10 text-center">
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{chu}</p>
      {goiY && <p className="mt-1 text-xs text-slate-400">{goiY}</p>}
    </div>
  );
}

/** Khung xương lúc đang tải. Giữ đúng chiều cao để trang không nhảy. */
export function DangTai({ dong = 3 }: { dong?: number }) {
  return (
    <div className="animate-pulse space-y-3 p-5">
      {Array.from({ length: dong }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-700" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-2.5 w-1/4 rounded bg-slate-100 dark:bg-slate-700/60" />
          </div>
        </div>
      ))}
    </div>
  );
}
